import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  SideNav,
  SideNavItems
} from 'carbon-components-react/lib/components/UIShell';
import { SCREEN_SIZE, USERSROLE } from 'types';
import LeftNavCommonMenuItem from './leftNav.CommonMenuItem';
import LeftNavAdminMenuItem from './leftNav.AdminMenuItem';
import LeftNavSysAdminMenuItem from './leftNav.SysAdminMenuItem';
import LeftNavShowHideMenu from './leftNav.ShowHideMenu';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectScreenSize,
  currentUser,
  selectIsSysAdminMode,
  selectIsShowHideTriggered,
  selectorAlertNotificationShowHide
} from 'redux/session/selectors';
import { toggleShowHide } from 'redux/session/actions';
import { useEventListener } from 'utilities/hooks.util';

const bigScreens = [SCREEN_SIZE.MAX, SCREEN_SIZE.XL];
const smallScreens = [SCREEN_SIZE.MD, SCREEN_SIZE.SM];

export const LeftHandSideNav: React.FC<Props> = (props: Props) => {
  const {
    disableNav = undefined,
    isSideNavExpanded,
    setIsSideNavExpanded
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const screenSize = useSelector(selectScreenSize);
  const user = useSelector(currentUser);
  const isSysAdminMode = useSelector(selectIsSysAdminMode);
  const isShowHideTriggered = useSelector(selectIsShowHideTriggered);
  const alertNotificationToggle = useSelector(
    selectorAlertNotificationShowHide
  );
  const dispatch = useDispatch();

  const [localUserRole, setLocalUserRole] = useState<string | undefined>();
  const [hasDraft, setHasDraft] = useState<boolean>(false);

  const handleClickOutside = useCallback(
    (evt: any) => {
      if (
        containerRef.current &&
        evt.target &&
        screenSize &&
        !bigScreens.includes(screenSize)
      ) {
        const burgerMenuButton = document
          .getElementsByClassName('burger-menu-button')
          ?.item(0);
        if (
          !containerRef.current.contains(evt.target) &&
          !burgerMenuButton?.contains(evt.target)
        ) {
          typeof setIsSideNavExpanded === 'function' &&
            setIsSideNavExpanded(false);
        }
      }
    },
    [screenSize, setIsSideNavExpanded]
  );

  useEffect(() => {
    setLocalUserRole(user?.is_trial ? USERSROLE.TRIALUSER : user?.role_type);
    setHasDraft(user?.hasDrafts);
  }, [user]);

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  const toggleShowHideHandler = () => {
    dispatch(toggleShowHide());
  };

  return (
    <>
      <SideNav
        ref={containerRef}
        isFixedNav={
          screenSize && bigScreens.includes(screenSize) ? true : false
        }
        expanded={
          isSideNavExpanded ||
          (screenSize && bigScreens.includes(screenSize) ? true : false)
        }
        isChildOfHeader={true}
        aria-label="Side navigation"
        className={`${
          alertNotificationToggle ? 'noti-alert-nav' : ''
        } left-hand-side-nav ${isShowHideTriggered ? 'showHideTrigger' : ''} ${
          isSideNavExpanded ? 'expanded' : ''
        }
        ${screenSize && smallScreens.includes(screenSize) ? 'sizeMobile' : ''} 
        `}
        data-testid="leftHandSideNav"
      >
        <SideNavItems isSideNavExpanded={true}>
          {isSysAdminMode ? (
            <LeftNavSysAdminMenuItem
              disableNav={disableNav === null ? undefined : disableNav}
            />
          ) : (
            <>
              <LeftNavCommonMenuItem
                disableNav={disableNav === null ? undefined : disableNav}
                isSmallScreen={
                  screenSize !== undefined && smallScreens.includes(screenSize)
                }
                hasDraft={hasDraft}
                userRole={localUserRole}
              />
              {screenSize &&
                (!smallScreens.includes(screenSize) ||
                  localUserRole === USERSROLE.TRIALUSER) &&
                user?.role_type_actual !== USERSROLE.USER && (
                  <LeftNavAdminMenuItem
                    disableNav={disableNav === null ? undefined : disableNav}
                  />
                )}
            </>
          )}
          {screenSize &&
            !smallScreens.includes(screenSize) &&
            process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true' && (
              <LeftNavShowHideMenu
                isExpanded={isShowHideTriggered}
                toggleShowHide={toggleShowHideHandler}
              />
            )}
        </SideNavItems>
      </SideNav>
    </>
  );
};

const propTypes = {
  disableNav: PropTypes.bool,
  isSideNavExpanded: PropTypes.bool.isRequired,
  setIsSideNavExpanded: PropTypes.func
};

type Props = PropTypes.InferProps<typeof propTypes>;

LeftHandSideNav.propTypes = propTypes;

export default LeftHandSideNav;
